/**
 * Game Context
 * Manages game state and operations
 */

import { useBridge } from '@/hooks/useBridge'
import type { GameInfo } from '@/services/bridge/types/bridge.types'
import {
  setAccessToken as setAxiosAccessToken,
  clearAccessToken as clearAxiosAccessToken,
} from '@/lib/axios'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react'

export interface TestStudent {
  studentId: string
  studentName: string
  avatarIconUrl: string
  avatarBackgroundNumber: number
  score: number
  startedAt: number
}

interface GameContextType {
  // 狀態
  accessToken: string | null
  gameInfo: GameInfo | null
  isEndGame: boolean
  isLoading: boolean
  error: string | null

  // 測試模式
  isTestMode: boolean
  testStudentList: TestStudent[]
  setIsTestMode: (isTestMode: boolean) => void
  setTestStudentList: (students: TestStudent[]) => void

  // 操作
  loadGameData: () => Promise<void>
  setIsEndGame: (isEndGame: boolean) => void

  // 計算屬性
  gameTitle: string
  gameId: string | null
}

const GameContext = createContext<GameContextType | undefined>(undefined)

interface GameProviderProps {
  children: React.ReactNode
  autoLoad?: boolean
}

export const GameProvider = ({
  children,
  autoLoad = true,
}: GameProviderProps) => {
  const {
    getAccessToken,
    getCurrentGameInfo,
    isLoading: bridgeLoading,
    error: bridgeError,
    isConnected: bridgeConnected,
  } = useBridge()

  // Game 特有的狀態
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [gameInfo, setGameInfo] = useState<GameInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEndGame, setIsEndGame] = useState(false)

  // 測試模式狀態
  const [isTestMode, setIsTestMode] = useState(false)
  const [testStudentList, setTestStudentList] = useState<TestStudent[]>([])

  /**
   * 載入遊戲資料（Token + 遊戲資訊）
   */
  const loadGameData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // 並行載入 token 和遊戲資訊
      const [token, info] = await Promise.all([
        getAccessToken(),
        getCurrentGameInfo(),
      ])

      // 設置 token 到 state 和 axios 全局配置
      setAccessToken(token)
      setAxiosAccessToken(token) // 設置到 axios

      setGameInfo(info)

      // 從 gameInfo 設置 isEndGame 初始值
      setIsEndGame(info.isEndGame)

      console.log('Game data loaded successfully:', { token, info })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load game data'
      setError(errorMessage)
      console.error('Failed to load game data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [getAccessToken, getCurrentGameInfo])

  // 自動載入（等待 Bridge 連接後）
  useEffect(() => {
    if (autoLoad && bridgeConnected) {
      loadGameData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, bridgeConnected]) // 當 Bridge 連接成功時載入

  // 同步 bridge 的 loading 狀態
  useEffect(() => {
    if (bridgeLoading) {
      setIsLoading(true)
    }
  }, [bridgeLoading])

  // 同步 bridge 的 error
  useEffect(() => {
    if (bridgeError) {
      setError(bridgeError)
    }
  }, [bridgeError])

  // Cleanup: 清除 axios token 當組件卸載
  useEffect(() => {
    return () => {
      clearAxiosAccessToken()
      console.log('GameContext unmounted, axios token cleared')
    }
  }, [])

  const contextValue: GameContextType = useMemo(
    () => ({
      // 狀態
      accessToken,
      gameInfo,
      isLoading,
      error,
      isEndGame,

      // 測試模式
      isTestMode,
      testStudentList,
      setIsTestMode,
      setTestStudentList,

      // 操作
      loadGameData,
      setIsEndGame,

      // 計算屬性
      gameTitle: gameInfo?.title || 'Unknown Game',
      gameId: gameInfo?.gameId || null,
    }),
    [
      accessToken,
      gameInfo,
      isLoading,
      error,
      loadGameData,
      isEndGame,
      isTestMode,
      testStudentList,
    ]
  )

  return (
    <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>
  )
}

/**
 * Hook to use Game Context
 */
export const useGame = (): GameContextType => {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}

export default GameContext
